import { useDoc } from '@/store/documentStore'
import { useUi } from '@/store/uiStore'
import type { ImageAnn } from '@/types'
import { pickWithInput } from './files'
import { uid } from './id'

export interface PreparedImage {
  src: string
  width: number
  height: number
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error('Unsupported image'))
    img.src = src
  })
}

/** Downscale large photos and pick PNG (alpha) or JPEG (photos) to keep PDFs small. */
export async function prepareImageFile(file: File, maxSide = 2000): Promise<PreparedImage> {
  const url = URL.createObjectURL(file)
  try {
    const img = await loadImage(url)
    const k = Math.min(1, maxSide / Math.max(img.naturalWidth, img.naturalHeight))
    const w = Math.max(1, Math.round(img.naturalWidth * k))
    const h = Math.max(1, Math.round(img.naturalHeight * k))
    const canvas = document.createElement('canvas')
    canvas.width = w
    canvas.height = h
    const ctx = canvas.getContext('2d')!
    ctx.drawImage(img, 0, 0, w, h)
    const keepAlpha =
      file.type === 'image/png' ||
      file.type === 'image/svg+xml' ||
      file.type === 'image/webp' ||
      file.type === 'image/gif'
    let hasAlpha = false
    if (keepAlpha) {
      const d = ctx.getImageData(0, 0, w, h).data
      for (let i = 3; i < d.length; i += 4 * 7)
        if (d[i] < 250) {
          hasAlpha = true
          break
        }
    }
    const src = hasAlpha ? canvas.toDataURL('image/png') : canvas.toDataURL('image/jpeg', 0.88)
    return { src, width: w, height: h }
  } finally {
    URL.revokeObjectURL(url)
  }
}

/** Make near-white pixels transparent (for photographed/scanned signatures) and crop. */
export async function cleanSignatureImage(src: string, threshold = 200): Promise<PreparedImage> {
  const img = await loadImage(src)
  const canvas = document.createElement('canvas')
  canvas.width = img.naturalWidth
  canvas.height = img.naturalHeight
  const ctx = canvas.getContext('2d')!
  ctx.drawImage(img, 0, 0)
  const data = ctx.getImageData(0, 0, canvas.width, canvas.height)
  const d = data.data
  for (let i = 0; i < d.length; i += 4) {
    const lum = 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2]
    if (lum > threshold) d[i + 3] = 0
    else d[i + 3] = Math.min(255, Math.round(((threshold - lum) / threshold) * 255 * 2.2))
  }
  ctx.putImageData(data, 0, 0)
  return trimCanvas(canvas)
}

/** Crop transparent margins, return PNG data URL. */
export function trimCanvas(canvas: HTMLCanvasElement, pad = 6): PreparedImage {
  const ctx = canvas.getContext('2d')!
  const { width, height } = canvas
  const d = ctx.getImageData(0, 0, width, height).data
  let x1 = width
  let y1 = height
  let x2 = -1
  let y2 = -1
  for (let y = 0; y < height; y++)
    for (let x = 0; x < width; x++)
      if (d[(y * width + x) * 4 + 3] > 8) {
        if (x < x1) x1 = x
        if (x > x2) x2 = x
        if (y < y1) y1 = y
        if (y > y2) y2 = y
      }
  if (x2 < 0) return { src: '', width: 0, height: 0 }
  x1 = Math.max(0, x1 - pad)
  y1 = Math.max(0, y1 - pad)
  x2 = Math.min(width - 1, x2 + pad)
  y2 = Math.min(height - 1, y2 + pad)
  const out = document.createElement('canvas')
  out.width = x2 - x1 + 1
  out.height = y2 - y1 + 1
  out.getContext('2d')!.drawImage(canvas, x1, y1, out.width, out.height, 0, 0, out.width, out.height)
  return { src: out.toDataURL('image/png'), width: out.width, height: out.height }
}

/** Drop an image onto the middle of the page the user is looking at, selected and ready to move. */
export function placeImage(img: PreparedImage, kind: ImageAnn['kind'], targetWidthPt?: number) {
  const doc = useDoc.getState()
  const ui = useUi.getState()
  const page = doc.pages[ui.currentPage] ?? doc.pages[0]
  if (!page || !img.src) return
  const aspect = img.height / img.width
  let w =
    targetWidthPt ?? Math.min(page.width * 0.5, kind === 'signature' ? 180 : kind === 'initials' ? 70 : 280)
  let h = w * aspect
  if (h > page.height * 0.6) {
    h = page.height * 0.6
    w = h / aspect
  }
  const id = uid('ann')
  doc.addAnnotation({
    id,
    pageId: page.id,
    type: 'image',
    x: (page.width - w) / 2,
    y: Math.min(page.height - h, page.height * 0.45),
    w,
    h,
    src: img.src,
    kind,
    color: '#000000',
    opacity: 1,
    createdAt: Date.now(),
    author: ui.author || undefined,
  })
  ui.setTool('select')
  ui.select([id])
}

export async function pickAndPlaceImage() {
  const [file] = await pickWithInput('image/png,image/jpeg,image/webp,image/gif,image/svg+xml')
  if (!file) return
  placeImage(await prepareImageFile(file), 'image')
}

const STAMP_COLORS = { green: '#15803d', red: '#b91c1c', blue: '#1d4ed8', gray: '#374151' }

export type StampKind =
  | 'check'
  | 'cross'
  | 'dot'
  | 'approved'
  | 'rejected'
  | 'draft'
  | 'confidential'
  | 'received'
  | 'paid'

export const STAMPS: { id: StampKind; label: string; color: string }[] = [
  { id: 'check', label: 'Checkmark', color: STAMP_COLORS.green },
  { id: 'cross', label: 'Cross', color: STAMP_COLORS.red },
  { id: 'dot', label: 'Dot', color: STAMP_COLORS.gray },
  { id: 'approved', label: 'Approved', color: STAMP_COLORS.green },
  { id: 'rejected', label: 'Rejected', color: STAMP_COLORS.red },
  { id: 'received', label: 'Received', color: STAMP_COLORS.blue },
  { id: 'paid', label: 'Paid', color: STAMP_COLORS.green },
  { id: 'draft', label: 'Draft', color: STAMP_COLORS.gray },
  { id: 'confidential', label: 'Confidential', color: STAMP_COLORS.red },
]

/** Render a crisp stamp image (vector-drawn at 4x) for placing on the page. */
export function renderStamp(kind: StampKind): { img: PreparedImage; widthPt: number } {
  const def = STAMPS.find((s) => s.id === kind)!
  const k = 4
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')!
  if (kind === 'check' || kind === 'cross' || kind === 'dot') {
    canvas.width = canvas.height = 24 * k
    ctx.scale(k, k)
    ctx.strokeStyle = def.color
    ctx.fillStyle = def.color
    ctx.lineWidth = 3
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctx.beginPath()
    if (kind === 'check') {
      ctx.moveTo(4, 13)
      ctx.lineTo(9.5, 18.5)
      ctx.lineTo(20, 5.5)
      ctx.stroke()
    } else if (kind === 'cross') {
      ctx.moveTo(5, 5)
      ctx.lineTo(19, 19)
      ctx.moveTo(19, 5)
      ctx.lineTo(5, 19)
      ctx.stroke()
    } else {
      ctx.arc(12, 12, 5, 0, Math.PI * 2)
      ctx.fill()
    }
    return {
      img: { src: canvas.toDataURL('image/png'), width: canvas.width, height: canvas.height },
      widthPt: 18,
    }
  }
  const text = def.label.toUpperCase()
  const date = new Date().toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
  ctx.font = `800 22px "Inter Variable", Arial, sans-serif`
  const tw = ctx.measureText(text).width
  const W = Math.ceil(Math.max(tw, 90) + 32)
  const H = 54
  canvas.width = W * k
  canvas.height = H * k
  ctx.scale(k, k)
  ctx.strokeStyle = def.color
  ctx.fillStyle = def.color
  ctx.lineWidth = 2.5
  const r = 8
  ctx.beginPath()
  ctx.roundRect(2, 2, W - 4, H - 4, r)
  ctx.stroke()
  ctx.font = `800 22px "Inter Variable", Arial, sans-serif`
  ctx.textAlign = 'center'
  ctx.textBaseline = 'alphabetic'
  ctx.fillText(text, W / 2, 30)
  ctx.font = `600 11px "Inter Variable", Arial, sans-serif`
  ctx.fillText(date, W / 2, 45)
  return { img: { src: canvas.toDataURL('image/png'), width: W * k, height: H * k }, widthPt: W * 0.9 }
}

export function placeStamp(kind: StampKind) {
  const { img, widthPt } = renderStamp(kind)
  placeImage(img, 'stamp', widthPt)
}

export function placeDateText() {
  const doc = useDoc.getState()
  const ui = useUi.getState()
  const page = doc.pages[ui.currentPage] ?? doc.pages[0]
  if (!page) return
  const s = ui.styleFor('text')
  const text = new Date().toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
  const id = uid('ann')
  const w = text.length * s.fontSize * 0.55 + 8
  doc.addAnnotation({
    id,
    pageId: page.id,
    type: 'text',
    x: (page.width - w) / 2,
    y: page.height * 0.45,
    w,
    h: s.fontSize * 1.2 + 4,
    text,
    color: s.color,
    opacity: 1,
    fontSize: s.fontSize,
    fontFamily: s.fontFamily,
    bold: s.bold,
    italic: s.italic,
    background: null,
    createdAt: Date.now(),
  })
  ui.setTool('select')
  ui.select([id])
}
